<template>
  <Dialog :open="isOpen" :disableOverlayClose="disableOverlayClose" @update:open="updateOpen">
    <DialogContent 
      class="max-w-full sm:max-w-[700px] md:max-w-[860px] flex flex-col max-h-[90vh]" 
      :disableOverlayClose="disableOverlayClose"
      @pointerDownOutside="(e) => disableOverlayClose && e.preventDefault()"
      @interactOutside="(e) => disableOverlayClose && e.preventDefault()"
      @close="handleClose"
    >
      <DialogHeader>
        <DialogTitle>{{ title }}</DialogTitle>
        <DialogDescription v-if="description">
          {{ description }}
        </DialogDescription>
      </DialogHeader>
      <div class="steps-modal-content flex-grow overflow-y-auto py-4" style="min-height:0;">
        <div v-if="loading" class="steps-loading flex items-center justify-center py-12">
          <div class="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
        </div>
        <div v-else class="steps-container" ref="stepsContainer">
          <Steps
            :initial-step="currentInitialStep"
            :show-step-titles="showStepTitles"
            @complete="onComplete"
            @step-change="onStepChange"
          >
            <slot></slot>
          </Steps>
        </div>
      </div>
      <DialogFooter v-if="$slots.footer">
        <slot name="footer"></slot>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import Dialog from '../dialog/Dialog.vue'
import DialogContent from '../dialog/DialogContent.vue'
import DialogHeader from '../dialog/DialogHeader.vue'
import DialogTitle from '../dialog/DialogTitle.vue'
import DialogDescription from '../dialog/DialogDescription.vue'
import DialogFooter from '../dialog/DialogFooter.vue'
import Steps from './Steps.vue'

const props = defineProps({
  title: {
    type: String,
    default: 'Multi-step Wizard'
  },
  description: {
    type: String,
    default: ''
  },
  open: {
    type: Boolean,
    default: false
  },
  initialStep: {
    type: Number,
    default: 0
  },
  showStepTitles: {
    type: Boolean,
    default: true
  },
  disableOverlayClose: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:open', 'complete', 'step-change'])

const isOpen = ref(props.open)
const currentInitialStep = ref(props.initialStep)
const loading = ref(true)
const stepsContainer = ref(null)

const closeButtonClicked = ref(false)

watch(isOpen, () => {
  closeButtonClicked.value = false
})

watch(() => props.open, (newValue) => {
  isOpen.value = newValue
  
  if (newValue) {
    currentInitialStep.value = 0
    loading.value = true
    
    setTimeout(() => {
      loading.value = false
    }, 300)
  }
})

const updateOpen = (value) => {
  if (!value && props.disableOverlayClose && !closeButtonClicked.value) {
    return;
  }
  
  isOpen.value = value
  emit('update:open', value)
}

const onComplete = () => {
  emit('complete')
  closeButtonClicked.value = true
  updateOpen(false)
}

const onStepChange = (step) => {
  emit('step-change', step)
}

const onClose = () => {
  closeButtonClicked.value = true
  updateOpen(false)
}

const handleClose = (event) => {
  if (event && event.source === 'button') {
    closeButtonClicked.value = true
  }
  updateOpen(false)
}

onMounted(() => {
  if (isOpen.value) {
    setTimeout(() => {
      loading.value = false
    }, 300)
  }
})
</script>

<style scoped>
.steps-modal-content {
  min-height: 176px;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.steps-modal-content::-webkit-scrollbar {
  display: none;
}
.steps-loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--background);
  z-index: 1001;
}
.steps-container {
  opacity: 0;
  animation: fadeIn 0.3s ease-in-out forwards;
  animation-delay: 0.1s;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style> 